import { createEmpty, extend, type Extent } from 'ol/extent';
import VectorLayer from 'ol/layer/Vector';
import type Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import type { LayerItem } from './types';
import { calculateExtentFromFeatures } from '../utils/calculateExtentWithAntimeridianSupport';

const getVectorSource = (item: LayerItem) => {
  if (!(item.layer instanceof VectorLayer)) {
    return undefined;
  }

  return (item.layer as VectorLayer<Feature<OlGeometry>>).getSource();
};

/**
 * Computes the combined extent of all VectorLayers in a list of LayerItems.
 * Returns undefined if no valid extent could be computed.
 *
 * When the display and data projections are provided the extent is calculated
 * from every child feature together with antimeridian support. Doing this per
 * layer instead would combine normalized and non-normalized coordinate
 * representations (e.g. a child wrapped to -190°..-170° next to one at +179°),
 * which `extend` turns into a world-spanning extent, so zooming to the group
 * would fit the entire globe (CLASSIC-10067).
 */
export function getCombinedExtent(
  layers: LayerItem[],
  displayProjection?: string,
  dataProjection?: string,
): Extent | undefined {
  if (displayProjection && dataProjection) {
    const features = layers.flatMap(
      (item) => getVectorSource(item)?.getFeatures() ?? [],
    );

    if (features.length === 0) {
      return undefined;
    }

    const extent = calculateExtentFromFeatures(
      features,
      displayProjection,
      dataProjection,
    );

    return extent && isFinite(extent[0]) ? extent : undefined;
  }

  const combined = createEmpty();
  let hasExtent = false;

  for (const item of layers) {
    const source = getVectorSource(item);
    if (source) {
      const ext = source.getExtent();
      if (ext && isFinite(ext[0])) {
        extend(combined, ext);
        hasExtent = true;
      }
    }
  }

  return hasExtent ? combined : undefined;
}
